import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { getVersion } from '@fc-shared/utils/data.utils';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { SidePopupComponent } from '@fc-core/components/side-popup.component';
import { NotificationsModule } from '@fc-core/notifications/notifications.module';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { setNavbarState } from '@fc-store/actions';
import { getNavbarState } from '@fc-store/selectors/ui.selector';
import { getShowNews } from '@fc-store/selectors/news.selectors';
import { HasFeatureModule } from '@fc-shared/directives/has-feature/has-feature.module';
import { AvatarModule } from '@fc-shared/ui/avatar/avatar.module';
import {
  currentUserHasLegacyPermission,
  getCurrentEmployee,
  getCurrentUser,
  getEmployeeById,
  selectRouterParamData,
} from '@fc-store/selectors';
import { AuthService, DialogsService, Employee, User } from '@fc-core';
import { PermissionsService } from '@fc-core/services/permissions.service';
import { PwaService } from '@fc-core/services/pwa-service';
import { QuickCreationComponent } from '@fc-core/components/header/quick-creation/quick-creation.component';
import { HelpCenterDialogComponent } from '@fc-core/components/header/help-center-dialog/help-center-dialog.component';
import { QuickCreationNav } from '@fc-core/components/header/quick-creation/quick-creation.nav';
import { environment } from '../../../../environments/environment';
import { AmplitudeService } from '@fc-core/services/amplitude.service';
import { FilesUploadUiStore } from '@fc-core/files-uploads/store/files-upload-ui.store';
import { CircularProgressComponent } from '@fc-core/files-uploads/components/file-card/circular-progress.component';
import { FilesUploadsService } from '@fc-core/files-uploads/services/files-uploads.service';
import { CoreInputModule } from '@fc-shared/ui/input/core/core-input.module';
import { EmployeeMenuComponent } from './employee-menu.component';
import { QuickCreationButtonComponent } from './quick-creation-button.component';
import { HeaderFacade } from './header.facade';
import { NgTagComponent } from '@fc-shared/ui/tags/ng-tag.component';
import { QuickSearchComponent } from 'src/app/search/quick-search/quick-search.component';
import { BreakpointsService } from '@fc-core/services/breakpoints.service';

@Component({
  selector: 'fc-header',
  imports: [
    RouterLink,
    NgButtonComponent,
    SidePopupComponent,
    NotificationsModule,
    IconComponent,
    HasFeatureModule,
    AvatarModule,
    NgButtonComponent,
    CircularProgressComponent,
    CoreInputModule,
    EmployeeMenuComponent,
    QuickCreationButtonComponent,
    NgTagComponent,
    QuickSearchComponent,
  ],
  templateUrl: './header.component.html',
  host: { class: 'print:hidden' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  store: Store = inject(Store);
  amplitude = inject(AmplitudeService);
  router = inject(Router);
  dialogs = inject(DialogsService);
  permissions: PermissionsService = inject(PermissionsService);
  pwaService: PwaService = inject(PwaService);
  authService = inject(AuthService);
  filesUploadUiStore = inject(FilesUploadUiStore);
  filesUploadsService = inject(FilesUploadsService);
  headerFacade = inject(HeaderFacade);
  breakpoints = inject(BreakpointsService);
  isHijacked = this.authService.tokenData?.is_hijacked;
  title: Signal<string> = this.store.selectSignal(
    selectRouterParamData('title'),
  );
  navbarState: Signal<boolean> = this.store.selectSignal(getNavbarState);
  currentEmployee: Signal<Employee | null | undefined> = computed(() => {
    const currentEmployee = this.store.selectSignal(getCurrentEmployee)();
    if (currentEmployee?.id) {
      return this.store.selectSignal(getEmployeeById(currentEmployee.id))();
    }
    return null;
  });
  currentUser: Signal<User> = this.store.selectSignal(getCurrentUser);
  canSwitchCustomer = this.store.selectSignal(
    currentUserHasLegacyPermission(
      'accounting_customer_can_switch_customer_view',
    ),
  );

  quickCreationNavs = this.headerFacade.quickCreationNavs;
  quickCreation = this.headerFacade.quickCreation;
  environment = environment.environment;

  logoLink =
    environment.environment === 'local' ? ['/components-overview'] : ['/'];
  showNews = this.headerFacade.showNews;
  showEvents = this.headerFacade.showEvents;
  unreadNewsCount = this.store.selectSignal(getShowNews);

  openActivity() {
    this.amplitude.track('topbar_click_activity');
    this.headerFacade.showNews.set(false);
    this.headerFacade.showEvents.set(true);
  }

  openNews() {
    this.amplitude.track('topbar_click_news');
    this.headerFacade.showNews.set(true);
    this.headerFacade.showEvents.set(false);
  }

  closeNews(): void {
    this.headerFacade.showNews.set(false);
  }

  closeEvents(): void {
    this.headerFacade.showEvents.set(false);
  }

  toggleNavbarState(): void {
    this.amplitude.track('topbar_click_menu');
    this.store.dispatch(setNavbarState({ state: !this.navbarState() }));
  }

  get version(): string | undefined {
    return getVersion();
  }

  get statusText(): string {
    return this.headerFacade.getStatusText(this.currentEmployee()!);
  }

  quickCreationClick(evt?: QuickCreationNav): void {
    this.headerFacade.navigateQuickCreation(evt);
  }

  triggerAnalytics(event: string): void {
    this.amplitude.track(event);
  }

  openDeveloperOptions() {
    this.headerFacade.openDeveloperOptions();
  }

  openConfirmLogout() {
    this.headerFacade.openConfirmLogout();
  }

  openQuickCreationMenu() {
    this.dialogs.openMobileDialog(QuickCreationComponent);
  }

  openMobileHelp() {
    this.amplitude.track('topbar_click_help');
    this.dialogs.openMobileDialog(HelpCenterDialogComponent);
  }

  showUploads() {
    this.filesUploadUiStore.showUploads();
  }

  close(): void {
    this.store.dispatch(setNavbarState({ state: false }));
  }

  openChangeCustomer() {
    if (this.canSwitchCustomer()) {
      this.headerFacade.openChangeCustomer();
    }
  }

  openStatusSetDialog(): void {
    this.headerFacade.openStatusSetDialog(
      this.currentEmployee()?.id,
      this.currentEmployee()?.setstatus,
    );
  }
}
